import { Component, OnInit, input, output, ChangeDetectorRef, computed } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Workflow } from "../service/Workflow";
import {
    DataIngestionService,
    INGESTION_POINT_TYPE_ID,
    IngestionFieldDefinition,
} from "../../data-ingestion/data.ingestion.service";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { S25RuleTreeModule } from "../../s25-rule-tree/s25.rule.tree.module";
import { WorkflowActionComponent } from "./workflow.action.component";
import { Bind } from "../../../decorators/bind.decorator";

interface HttpHeader {
    name: string;
    value: string;
}

@TypeManagerDecorator("workflow-actions")
@Component({
    selector: "workflow-actions",
    template: `
        <div class="workflow-actions">
            @if (!hideStageHeader()) {
                <div class="actions-header">
                    <h4>{{ stage()?.stageName ? stage()!.stageName + " Actions" : "Stage Actions" }}</h4>
                    @if (!hideAddButtons()) {
                        <div class="action-buttons">
                            <button class="aw-button aw-button--primary" (click)="addHttpAction()">
                                Add HTTP Action
                            </button>
                            <button class="aw-button aw-button--secondary" (click)="addSystemAction()">
                                Add System Action
                            </button>
                        </div>
                    }
                </div>
            }

            @if (actions().length === 0) {
                <div class="no-actions">
                    No actions defined. Add HTTP actions to be executed when this workflow state is reached.
                </div>
            }

            @if (actions().length > 0) {
                <div class="actions-list">
                    @for (action of actions(); track action.id || action.workflowActionId; let idx = $index) {
                        <div class="action-card">
                            <div class="action-header">
                                <div class="action-info">
                                    <span class="action-type-badge" [class.system-action]="action.isSystemAction">
                                        {{ action.isSystemAction ? "SYSTEM" : "HTTP REQUEST" }}
                                    </span>
                                    <span class="sort-order-display">Order: {{ idx + 1 }}</span>
                                </div>
                                <div class="action-controls">
                                    <div class="move-buttons">
                                        <button
                                            class="aw-button aw-button--outline move-btn"
                                            (click)="moveActionUp(action)"
                                            [disabled]="idx === 0"
                                            title="Move up"
                                        >
                                            ↑
                                        </button>
                                        <button
                                            class="aw-button aw-button--outline move-btn"
                                            (click)="moveActionDown(action)"
                                            [disabled]="idx === actions().length - 1"
                                            title="Move down"
                                        >
                                            ↓
                                        </button>
                                    </div>
                                    <button
                                        class="aw-button aw-button--danger--outline"
                                        (click)="removeAction(action)"
                                        title="Remove action"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                            <s25-ng-workflow-action
                                [action]="action"
                                [hasRules]="!!stageId()"
                                [inputIdGenerator]="getNextTempActionInputId"
                            />
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: `
        .workflow-actions {
            border: 1px solid #dee2e6;
            border-radius: 8px;
            background: #f9f9f9;
            padding: 16px;
        }

        .actions-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;
        }

        .actions-header h4 {
            margin: 0;
            color: #333;
        }

        .action-buttons {
            display: flex;
            gap: 8px;
        }

        .no-actions {
            text-align: center;
            padding: 32px;
            color: #666;
            font-style: italic;
            background: white;
            border: 1px dashed #dee2e6;
            border-radius: 6px;
        }

        .actions-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .action-card {
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            overflow: hidden;
        }

        .action-header {
            background: #f8f9fa;
            padding: 12px 16px;
            border-bottom: 1px solid #dee2e6;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .action-controls {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .move-buttons {
            display: flex;
            gap: 4px;
        }

        .move-btn {
            width: 32px;
            height: 32px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: bold;
        }

        .action-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .action-type-badge {
            background: #007bff;
            color: white;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: bold;
            text-transform: uppercase;
        }

        .action-type-badge.system-action {
            background: #28a745;
        }

        .sort-order-display {
            color: #666;
            font-size: 12px;
            font-weight: normal;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25DropdownPaginatedModule, S25RuleTreeModule, WorkflowActionComponent],
})
export class WorkflowActionsComponent implements OnInit {
    workflowProcess = input<Workflow.WorkflowProcess>();
    stageId = input<number | string>();
    stage = input<Workflow.WorkflowStage>();
    actions = input<Workflow.WorkflowAction[]>([]);
    actionsChange = output<Workflow.WorkflowAction[]>();
    hideAddButtons = input<boolean>(false);
    hideStageHeader = input<boolean>(false);

    // Per-action form contexts and available fields
    private actionFormContexts = new Map<string, DropDownItem | null>();
    private actionAvailableFields = new Map<string, IngestionFieldDefinition[]>();

    private actionHeaders = new Map<string, HttpHeader[]>(); // Store headers for each action

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {
        if (this.actions().length) {
            let len = this.actions().length;
            for (let idx = len - 1; idx >= 0; idx--) {
                const action = this.actions()[idx];
                if (!action) {
                    this.actions().splice(idx, 1);
                }
            }
            if (this.actions().length !== len) {
                this.actionsChange.emit(this.actions());
            }

            // Initialize headers from existing actions
            this.actions().forEach((action) => {
                this.initializeActionHeaders(action);
                this.initializeActionFormContext(action);
            });
        }
    }

    async loadActionAvailableFields(action: Workflow.WorkflowAction, ingestionPointId: number): Promise<void> {
        const actionKey = this.getActionKey(action);
        try {
            const fields = await DataIngestionService.getFieldDefinitions(ingestionPointId);
            this.actionAvailableFields.set(actionKey, fields || []);
        } catch (error) {
            console.error("Error loading field definitions for action:", error);
            this.actionAvailableFields.set(actionKey, []);
        }
        this.cd.detectChanges();
    }

    addHttpAction() {
        const newAction: Workflow.WorkflowAction = {
            id: this.getNextTempActionId(),
            actionType: "HTTP_REQUEST",
            sortOrder: this.actions().length,
            httpMethod: "POST",
            httpUrl: "",
            httpHeaders: '{"Content-Type": "application/json"}',
            jsonPayloadTemplate: "",
            description: "",
        };

        // Initialize headers for the new action
        this.initializeActionHeaders(newAction);

        // Add the new action to the actions array in place
        this.actions().push(newAction);
        this.actionsChange.emit(this.actions());
    }

    addSystemAction() {
        const newAction: Workflow.WorkflowAction = {
            id: this.getNextTempActionId(),
            actionType: "SYSTEM_ACTION",
            isSystemAction: true,
            sortOrder: this.actions().length,
            description: "",
            inputs: [],
        };

        // Add the new action to the actions array in place
        this.actions().push(newAction);
        this.actionsChange.emit(this.actions());
    }

    removeAction(actionToRemove: Workflow.WorkflowAction) {
        const actions = this.actions();
        const index = actions.findIndex(
            (action) =>
                (action.id || action.workflowActionId) === (actionToRemove.id || actionToRemove.workflowActionId),
        );
        if (index > -1) {
            actions.splice(index, 1);
            this.actionsChange.emit(actions);
        }
    }

    moveActionUp(action: Workflow.WorkflowAction) {
        const index = this.actions().findIndex(
            (a) => a.workflowActionId === action.workflowActionId || a.id === action.id,
        );
        if (index > 0) {
            const actions = this.actions();
            [actions[index - 1], actions[index]] = [actions[index], actions[index - 1]];
            this.updateSortOrders(actions);
            this.actionsChange.emit(actions);
        }
    }

    moveActionDown(action: Workflow.WorkflowAction) {
        const index = this.actions().findIndex(
            (a) => a.workflowActionId === action.workflowActionId || a.id === action.id,
        );
        const actions = this.actions();
        if (index < actions.length - 1) {
            [actions[index], actions[index + 1]] = [actions[index + 1], actions[index]];
            this.updateSortOrders(actions);
            this.actionsChange.emit(actions);
        }
    }

    private updateSortOrders(actions: Workflow.WorkflowAction[]) {
        actions.forEach((action, index) => {
            action.sortOrder = index;
        });
    }

    private getActionKey(action: Workflow.WorkflowAction): string {
        if (!action) {
            return "null";
        }
        return (action.id || action.workflowActionId || "new").toString();
    }

    private initializeActionHeaders(action: Workflow.WorkflowAction) {
        const actionKey = this.getActionKey(action);
        const headers: HttpHeader[] = [];

        if (action.httpHeaders) {
            try {
                const headerObj = JSON.parse(action.httpHeaders);
                Object.entries(headerObj).forEach(([name, value]) => {
                    headers.push({ name, value: String(value) });
                });
            } catch (error) {
                console.warn("Failed to parse headers for action", actionKey, error);
            }
        }

        // Ensure at least one header (Content-Type) exists for new actions
        if (headers.length === 0) {
            headers.push({ name: "Content-Type", value: "application/json" });
        }

        this.actionHeaders.set(actionKey, headers);
    }

    private async initializeActionFormContext(action: Workflow.WorkflowAction) {
        const actionKey = this.getActionKey(action);

        // If action has form context, initialize it
        if (action.actionItemId && action.actionItemTypeId === INGESTION_POINT_TYPE_ID) {
            try {
                const ingestionPoint = await DataIngestionService.getIngestionPoint(action.actionItemId);
                if (ingestionPoint) {
                    const dropdownItem: DropDownItem = {
                        txt: ingestionPoint.ingestionPointName || `Form ${ingestionPoint.ingestionPointId}`,
                        val: ingestionPoint.ingestionPointId,
                        itemTypeId: INGESTION_POINT_TYPE_ID,
                        itemId: ingestionPoint.ingestionPointId,
                        itemName: ingestionPoint.ingestionPointName,
                        data: ingestionPoint,
                    };

                    this.actionFormContexts.set(actionKey, dropdownItem);
                    await this.loadActionAvailableFields(action, ingestionPoint.ingestionPointId);
                }
            } catch (error) {
                console.error("Error initializing form context for action:", error);
            }
        }
    }

    private getNextTempActionId(): number {
        return this.actions().reduce((minId, action) => Math.min(minId, action.id || -1), -1) - 1;
    }

    @Bind
    getNextTempActionInputId(): number {
        let minId = -1;

        // Check action input IDs across all actions
        for (const action of this.actions()) {
            if (action.inputs) {
                minId = action.inputs.reduce((min, input) => Math.min(min, input.id || -1), minId);
            }
        }

        return minId - 1;
    }

    // Action Rules Methods
    private emitActionsChange() {
        this.actionsChange.emit(this.actions());
        this.cd.detectChanges();
    }
}
